/**
 * Session Models
 */
export interface Session {
    startTimestamp: number;
    endTimestamp: number;
    duration: number;
    name: string;
    address: string;
    elevation: number;
    temperature: number;
    humidity: number;
    location: string;
    normalizedElevation: number;
    normalizedTemperature: number;
    normalizedBallType: any;
    sessionQuality?: number;
    shots: Shot[];
    clubs: Club[];
    targets: Target[];
    drillTargets?: any;
}

export enum MeasuringSystem {
    imperial = "imperial",
    metric = "metric"
}
export interface SessionExtra extends Session {
    isLoading: boolean;
    status: string;
    starCount: number;
    isNormalized: boolean;
    measuringSystem: MeasuringSystem
    temperatureConverted: string;
    isAnyShotSelected: Boolean;
    durationString: string;

}

/**
 * Club Models
 */
export interface Club {
    clubId: string;

    isActive: boolean;
    isShowShots: boolean;
    isNormalized: boolean;

    clubName: string;
    clubType: string;
    clubCategory: string;
    clubNickname: string;
    clubColor: string;
    clubIcon: string;

    totalShots: number;
    average: Average;

    shots: ShotExtra[];
    selectedTimestamp: number;
}
/**
 * Target Models
 */
export interface Target {
    targetId: string;
    isActive: boolean;
    isShowShots: boolean;
    isNormalized: boolean;
    totalShots: number;
    average: Average;

    shots: ShotExtra[];
    drillTarget?: any;
}

export type DataCard = {
    uom: string;
    value: string;
    label: string;
}

export type Average = {
    carryDistance: DataCard;
    spinRate: DataCard;
    distanceToPin: DataCard;
    ballSpeed: DataCard;
    clubSpeed: DataCard;
    smashFactor: DataCard;
    apex: DataCard;
    launchAngle: DataCard;
    // KWSA-42: shown by the Simulator tab layout.
    totalDistance: DataCard;
    descentAngle: DataCard;

    clubPath: DataCard;
    faceAngle: DataCard;
    attackAngle: DataCard;
    faceToPath: DataCard;

    spinAxis: DataCard;
    horizontalLaunchAngle: DataCard;
    side: DataCard;

    quality: DataCard;
}

/**
 * Shot Models
 */
export interface NormalizedValues {
    apex?: number;
    carryDistance?: number;
    side?: number;
    sideTotal?: number;
    totalDistance?: number;
    distanceToPin?: number;
}
export interface Shot {
    pointId: string;
    lmDrillTargetId: string;
    timestamp: number;
    isFavorite: boolean;
    clubCategory: string;
    clubId: string;
    clubName: string;
    clubType: string;
    clubColor: string;
    clubSpeed: number;
    ballSpeed: number;
    smashFactor: number;
    attackAngle: number;
    clubPath: number;
    launchAngle: number;
    descentAngle: number;
    horizontalLaunchAngle: number;
    faceAngle: number;
    spinRate: number;
    spinAxis: number;
    carryDistance: number;
    totalDistance: number;
    side: number;
    sideTotal: number;
    apex: number;
    ballDirection: string;
    ballCurve: number;
    impactUrl: string;
    videoUrl: string;
    clubSpeedValid: number;
    ballSpeedValid: number;
    smashFactorValid: number;
    attackAngleValid: number;
    clubPathValid: number;
    launchAngleValid: number;
    descentAngleValid: number;
    horizontalLaunchAngleValid: number;
    faceAngleValid: number;
    spinRateValid: number;
    spinAxisValid: number;
    carryDistanceValid: number;
    totalDistanceValid: number;
    sideValid: number;
    sideTotalValid: number;
    apexValid: number;
    xFit: number[];
    yFit: number[];
    zFit: number[];
    normalizedValues?: NormalizedValues;
    shotQuality: number,
    targetDistance: number,
    distanceToPin: number,
}

export type ConvertedData = {
    valueString: string;
    value: number;
    side?: string;
    sideValueString?: string;
}
export interface ShotExtra extends Shot {
    shotIndex: number,
    faceToPath: number,
    faceToPathConverted: ConvertedData;
    carryDistanceConverted: ConvertedData;
    distanceToPinConverted: ConvertedData;
    spinRateConverted: ConvertedData;
    ballSpeedConverted: ConvertedData;
    clubSpeedConverted: ConvertedData;
    smashFactorConverted: ConvertedData;
    apexConverted: ConvertedData;
    launchAngleConverted: ConvertedData;

    clubPathConverted: ConvertedData;
    faceAngleConverted: ConvertedData;
    attackAngleConverted: ConvertedData;

    spinAxisConverted: ConvertedData;
    descentAngleConverted: ConvertedData;
    horizontalLaunchAngleConverted: ConvertedData;
    sideConverted: ConvertedData;

    isActive: boolean;
    isSelected: boolean;
    isNormalized: boolean;
    selectedTimestamp: number;

    timestampConverted?: String;
    normalizedApexConverted?: ConvertedData;
    normalizedDistanceToPinConverted?: ConvertedData;
    normalizedCarryDistanceConverted?: ConvertedData;
    sideTotalConverted?: ConvertedData;
    normalizedSideTotalConverted?: ConvertedData;
    normalizedSideConverted?: ConvertedData;
    totalDistanceConverted?: ConvertedData;
    normalizedTotalDistanceConverted?: ConvertedData;

    clubIcon?: string;

}


